// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipToState,
    getShipTosDataView,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Pages/Addresses/Handlers/LoadShipTos";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    BillToModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { createContext, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import SelectableCard from "@store/Library/Components/Toolkit/SelectableCard";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { addressProps } from "@store/Themes/common/props/address";
import { inlineFormProps } from "@store/Themes/common/props/inlineForm";
import { typographyGetNoResultsAltProps } from "@store/Themes/common/props/typography";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";

/* Store Custom Code - Start */
import { useIsB2BCustomer } from "@store/Framework/Store/Data/BillTos/BillTosHooks";
/* Store Custom Code - End */

const AddressBookSearchBox = getWidgetComponent("Addresses/AddressBookSearchBox");
const CreateAddressButton = getWidgetComponent("Addresses/CreateAddressButton");
/* Starter Custom Code - End */

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
    getShipTosParameter: state.pages.addresses.getShipTosParameter,
    currentBillTo: getCurrentBillToState(state).value,
    currentShipTo: getCurrentShipToState(state).value,
    countries: getCurrentCountries(state),
    addressFieldDataView: getAddressFieldsDataView(state),
});

const mapDispatchToProps = {
    setCurrentShipTo,
    loadShipTos,
    updateShipTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressBookStyles {
    gridContainer?: GridContainerProps;
    titleTextGridItem?: GridItemProps;
    addressHeaderGridItem?: GridItemProps;
    titleText?: TypographyProps;
    noAddressesText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    addressCardGridItem?: GridItemProps;
    addressFormModal?: ModalPresentationProps;
    addressCard?: AddressCardStyles;
    // Starter Custom Code
    searchWrapper?: InjectableCss;
}

interface AddressCardStyles {
    gridContainer?: GridContainerProps;
    addressInfoDisplayGridItem?: GridItemProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    actionsGridItem?: GridItemProps;
    actionOverflowIcon?: IconPresentationProps;
    actionUseAsShippingAddressButton?: ButtonPresentationProps;
    actionEditLink?: LinkPresentationProps;
    bottomBorderGridItem?: GridItemProps;
    bottomBorder?: InjectableCss;
    overflowMenu?: OverflowMenuPresentationProps;
}

export const addressBookStyles: AddressBookStyles = {
    gridContainer: {
        gap: 20,
        css: spacingStyles(pxToRem(20), null),
    },
    noAddressesText: typographyGetNoResultsAltProps({ spaced: true }),
    addressCardGridItem: { width: [12, 6, 6, 4, 4] },
    addressCard: {
        actionEditLink: addressProps.linkEdit,
        addressInfoDisplay: {
            wrapper: addressProps.wrapper,
        },
    },
    addressFormModal: { sizeVariant: "medium" },
    searchWrapper: inlineFormProps.wrapper,
};

const styles = addressBookStyles;

type ContextType = {
    isSettingShipTo: boolean;
    setIsSettingShipTo: (value: boolean) => void;
};

export const AddressBookContext = createContext<ContextType>({
    isSettingShipTo: false,
    setIsSettingShipTo: value => {},
});

const AddressBook: React.FC<Props> = ({
    id,
    shipTosDataView: baseShipTosDataView, // Starter Custom Code
    loadShipTos,
    setCurrentShipTo,
    updateShipTo,
    addressFieldDataView: baseAddressFieldDataView, // Starter Custom Code
    countries,
    currentBillTo,
    currentShipTo,
    getShipTosParameter,
}) => {
    const isB2BCustomer = useIsB2BCustomer(); // Store Custom Code
    const [isSettingShipTo, setIsSettingShipTo] = useState(false);
    const shipTosDataView = usePersistentDataView(baseShipTosDataView); // Starter Custom Code
    const addressFieldDataView = usePersistentDataView(baseAddressFieldDataView); // Starter Custom Code

    React.useEffect(() => {
        if (!baseShipTosDataView.value && !baseShipTosDataView.isLoading /* Starter Custom Code */) {
            loadShipTos();
        }
    });

    /* Starter Custom Code - Start */
    const component = (
        <DataViewLoadingOverlay
            loading={shipTosDataView.isLoading || addressFieldDataView.isLoading}
            loadComplete={Boolean(shipTosDataView.value && addressFieldDataView.value)}
        >
            {shipTosDataView.value?.length ? (
                <GridContainer {...styles.gridContainer}>
                    {shipTosDataView.value.map(shipTo => (
                        <GridItem {...styles.addressCardGridItem} key={shipTo.id.toString()}>
                            <AddressCard
                                shipTo={shipTo}
                                setCurrentShipTo={setCurrentShipTo}
                                updateShipTo={updateShipTo}
                                billToAddressFields={addressFieldDataView.value?.billToAddressFields}
                                shipToAddressFields={addressFieldDataView.value?.shipToAddressFields}
                                countries={countries}
                                currentBillTo={currentBillTo}
                                currentShipTo={currentShipTo}
                            />
                        </GridItem>
                    ))}
                </GridContainer>
            ) : (
                <Typography {...styles.noAddressesText}>
                    {getShipTosParameter.filter
                        ? siteMessage("Addresses_NoResultsFound")
                        : siteMessage("Addresses_NoAddressesFound")}
                </Typography>
            )}
        </DataViewLoadingOverlay>
    );

    return (
        <AddressBookContext.Provider value={{ isSettingShipTo, setIsSettingShipTo }}>
            <CardListHeading heading={translate("Address Book")} />
            <StyledWrapper {...styles.searchWrapper}>
                <AddressBookSearchBox />
                {!isB2BCustomer /* Store Custom Code */ && <CreateAddressButton />}
            </StyledWrapper>
            {component}
        </AddressBookContext.Provider>
    );
    /* Starter Custom Code - End */
};
interface AddressCardProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
}

const AddressCard: React.FunctionComponent<AddressCardProps> = (props: AddressCardProps) => {
    const { shipTo } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    /* Starter Custom Code - Start */
    return (
        <AddressActions
            shipTo={shipTo}
            updateShipTo={props.updateShipTo}
            setCurrentShipTo={props.setCurrentShipTo}
            currentBillTo={props.currentBillTo}
            currentShipTo={props.currentShipTo}
            countries={props.countries}
            shipToAddressFields={props.shipToAddressFields}
            billToAddressFields={props.billToAddressFields}
        >
            <AddressInfoDisplay
                firstName={shipTo.firstName}
                lastName={shipTo.lastName}
                companyName={shipTo.companyName}
                attention={shipTo.attention}
                address1={shipTo.address1}
                address2={shipTo.address2}
                address3={shipTo.address3}
                address4={shipTo.address4}
                city={shipTo.city}
                state={shipTo.state ? shipTo.state.abbreviation : undefined}
                postalCode={shipTo.postalCode}
                country={shipTo.country ? shipTo.country.abbreviation : undefined}
                phone={shipTo.phone}
                fax={shipTo.fax}
                email={shipTo.email}
                extendedStyles={componentStyles.addressInfoDisplay}
            />
        </AddressActions>
    );
    /* Starter Custom Code - End */
};

interface AddressActionsProps {
    shipTo: ShipToModel;
    currentBillTo?: BillToModel;
    currentShipTo?: ShipToModel;
    countries?: CountryModel[];
    billToAddressFields?: AddressFieldDisplayCollectionModel;
    shipToAddressFields?: AddressFieldDisplayCollectionModel;
    setCurrentShipTo: Props["setCurrentShipTo"];
    updateShipTo: Props["updateShipTo"];
}

const AddressActions: React.FunctionComponent<React.PropsWithChildren<AddressActionsProps>> = (
    { children, ...props } /* Starter Custom Code */,
) => {
    const isB2BCustomer = useIsB2BCustomer(); // Store Custom Code

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const { shipTo, currentShipTo } = props;

    const addressBookContext = useContext(AddressBookContext);

    /* Starter Custom Code - Start */
    const [isLoading, setIsLoading] = React.useState(false);
    const trackShippingInfoChanged = ga.use.trackShippingInfoChanged();
    const trackNewShippingInfoSet = ga.use.trackNewShippingInfoSet();
    /* Starter Custom Code - End */

    const useAsShippingAddressHandler = (e: any) => {
        e.preventDefault();
        addressBookContext.setIsSettingShipTo(true);
        setIsLoading(true); // Starter Custom Code

        props.setCurrentShipTo({
            shipToId: shipTo.id,
            onSuccess: () => {
                addressBookContext.setIsSettingShipTo(false);
                /* Starter Custom Code - Start */
                setIsLoading(false);
                trackNewShippingInfoSet();
                /* Starter Custom Code - End */
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                }
            },
        });
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const editClickHandler = () => {
        setModalIsOpen(true);
    };

    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };

    const formSubmitHandler = (e: any, address: BaseAddressModel) => {
        e.preventDefault();
        const { currentBillTo, shipTo } = props;
        if (!currentBillTo || !shipTo) {
            return;
        }

        props.updateShipTo({
            billToId: currentBillTo.id,
            shipTo: {
                ...shipTo,
                ...address,
            },
            expand: ["validation"],
        });

        trackShippingInfoChanged(); // Starter Custom Code
        modalCloseHandler();
    };

    const { countries, shipToAddressFields } = props;
    const componentStyles: AddressCardStyles = styles.addressCard || {};

    return (
        <>
            {/* Starter Custom Code - Start */}
            <SelectableCard
                onSelect={useAsShippingAddressHandler}
                loading={isLoading}
                label={translate("Use as Shipping Address")}
                selected={shipTo.id === currentShipTo?.id}
            >
                {children}
                {!isB2BCustomer /* Store Custom Code */ && (
                    <Link {...componentStyles.actionEditLink} onClick={editClickHandler}>
                        {translate("Edit")}
                        <VisuallyHidden>{translate("Shipping Information")}</VisuallyHidden>
                    </Link>
                )}
            </SelectableCard>
            {/* Starter Custom Code - End */}
            <Modal
                {...styles.addressFormModal}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
                headline={translate("Edit Shipping Information")}
            >
                {countries && shipToAddressFields && (
                    <CustomerAddressForm
                        address={shipTo}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                    />
                )}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressBook),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
